import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import supabase from '../utils/supabaseClient';
import { trackEvent } from '../utils/analytics';
import './ProtectedRoute.css';

function ProtectedRoute() {
  const [status, setStatus] = useState('checking'); // checking | needsAuth | ready
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    const applySession = session => {
      if (!mounted) return;
      if (!session) {
        setStatus(prev => {
          if (prev !== 'needsAuth') {
            trackEvent('auth_start_page', { page: location.pathname });
          }
          return 'needsAuth';
        });
        return;
      }
      const roles = session.user?.user_metadata?.roles || [];
      if (roles.includes('banned')) {
        navigate('/blocked');
        return;
      }
      setStatus('ready');
    };

    const checkAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      applySession(session);
    };

    checkAuth();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    return () => {
      mounted = false;
      listener?.subscription.unsubscribe();
    };
  }, [navigate, location.pathname]);

  if (status === 'checking') {
    return (
      <div className="auth-gate">
        <div className="auth-gate__panel">
          <p className="auth-gate__lead">Checking sign-in…</p>
        </div>
      </div>
    );
  }

  if (status === 'needsAuth') {
    const redirect = `${location.pathname}${location.search || ''}`;
    const authTo = `/auth?redirect=${encodeURIComponent(redirect)}`;
    return (
      <div className="auth-gate">
        <div className="auth-gate__panel">
          <p className="auth-gate__eyebrow">Sign in required</p>
          <h1 className="auth-gate__title">Sign in to continue</h1>
          <p className="auth-gate__lead">
            This page needs an account. Sign in or create one, then we’ll bring you back here.
          </p>
          <div className="auth-gate__actions">
            <Link className="auth-gate__btn auth-gate__btn--primary" to={authTo}>
              Sign in
            </Link>
            <Link className="auth-gate__btn auth-gate__btn--secondary" to="/">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <Outlet />;
}

export default ProtectedRoute;
